import React, { useState, useEffect, useRef } from 'react';
import { Bell, AlertTriangle, Info, CheckCircle, XCircle, X } from 'lucide-react';
import { AdminNotification, AdminNotificationService } from '../../services/adminNotificationService';
import { bugReportService } from '../../services/bugReportService';
import { Customer, Booking, Instructor } from '../../types/crm';
import { QuoteWithDetails } from '../../types/quotes';
import { supabase } from '../../lib/supabase';
import { User } from '@supabase/supabase-js';
import { formatDateTimeUk } from '../../utils/paymentFormUtils';
import { getRoleFromUser } from '../../types/roles';

const SUPERADMIN_DISMISSED_KEY = 'dismissed_superadmin_notifications';

function getDismissedSuperAdmin(): Set<string> {
  try {
    const stored = localStorage.getItem(SUPERADMIN_DISMISSED_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function dismissSuperAdminNotification(notificationId: string): void {
  try {
    const dismissed = getDismissedSuperAdmin();
    dismissed.add(notificationId);
    localStorage.setItem(SUPERADMIN_DISMISSED_KEY, JSON.stringify([...dismissed]));
  } catch (e) {
    console.warn('Failed to save dismissed super admin notification', e);
  }
}

interface NotificationBellProps {
  customers: Customer[];
  bookings: Booking[];
  instructors: Instructor[];
  quotes?: QuoteWithDetails[];
  onNavigate?: (url: string) => void;
  /** When true, show only super-admin notifications (e.g. feature requests). Admins do not see these. */
  isSuperAdmin?: boolean;
  /** When user clicks an action that opens a Super Admin tab (e.g. Feature Requests). */
  onSuperAdminTabClick?: (tab: string) => void;
  /** Current authenticated user – used to fetch personal task/reminder notifications */
  currentUser?: User | null;
  /** Called when the user clicks a task/reminder notification action */
  onTasksClick?: () => void;
}

export function NotificationBell({ customers, bookings, instructors, quotes, onNavigate, isSuperAdmin, onSuperAdminTabClick, currentUser, onTasksClick }: NotificationBellProps) {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const generateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (generateTimerRef.current) clearTimeout(generateTimerRef.current);
    generateTimerRef.current = setTimeout(() => {
      void generateNotifications();
    }, 400);
    return () => {
      if (generateTimerRef.current) clearTimeout(generateTimerRef.current);
    };
  }, [customers, bookings, instructors, quotes, isSuperAdmin, currentUser?.id]);

  const generateNotifications = async () => {
    setIsLoading(true);
    try {
      if (isSuperAdmin) {
        const dismissed = getDismissedSuperAdmin();
        const openCount = await bugReportService.getOpenBugReportCount();
        const notifId = 'superadmin-feature-requests';
        const newNotifications: AdminNotification[] = [];
        if (openCount > 0 && !dismissed.has(notifId)) {
          newNotifications.push({
            id: notifId,
            type: 'info',
            title: 'Feature request(s) from admins',
            message: `${openCount} feature request${openCount !== 1 ? 's' : ''} need review.`,
            action: { label: 'View feature requests', url: 'superadmin:bug-reports' },
            priority: openCount > 3 ? 'high' : 'medium',
            category: 'operations',
            createdAt: new Date().toISOString(),
          });
        }
        setNotifications(newNotifications);
      } else {
        const businessNotifications = AdminNotificationService.generateNotifications(customers, bookings, instructors, quotes);

        let taskNotifications: AdminNotification[] = [];
        let ownerDelegatedNotifications: AdminNotification[] = [];
        if (currentUser?.id) {
          const isOwnerUser = getRoleFromUser(currentUser) === 'owner';
          const [tasksResult, remindersResult, delegatedDoneResult] = await Promise.all([
            supabase
              .from('admin_tasks')
              .select('id, text, completed, scheduled_date, scheduled_time, priority')
              .eq('completed', false),
            supabase
              .from('customer_reminders')
              .select('id, reminder_text, is_completed, reminder_date, reminder_time, priority, customer_name')
              .eq('is_completed', false),
            isOwnerUser
              ? supabase
                  .from('admin_tasks')
                  .select('id, text, completed, completed_at, assignee_name, completed_by')
                  .eq('assigned_by_user_id', currentUser.id)
                  .eq('completed', true)
                  .not('completed_at', 'is', null)
                  .order('completed_at', { ascending: false })
                  .limit(20)
              : Promise.resolve({ data: [], error: null }),
          ]);

          taskNotifications = AdminNotificationService.generateTaskNotifications(
            tasksResult.data || [],
            remindersResult.data || []
          );

          if (isOwnerUser && !delegatedDoneResult.error) {
            ownerDelegatedNotifications = AdminNotificationService.generateOwnerDelegatedTaskNotifications(
              delegatedDoneResult.data || []
            );
          }
        }

        const combined = [...ownerDelegatedNotifications, ...taskNotifications, ...businessNotifications];
        combined.sort((a, b) => {
          const order = { critical: 4, high: 3, medium: 2, low: 1 };
          return order[b.priority] - order[a.priority];
        });

        setNotifications(combined);
      }
    } catch (error) {
      console.error('Error generating notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = (notificationId: string) => {
    if (isSuperAdmin) {
      dismissSuperAdminNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } else {
      setNotifications(prev => AdminNotificationService.dismissNotification(prev, notificationId));
    }
  };

  const handleAction = (notification: AdminNotification) => {
    if (notification.action?.onClick) {
      notification.action.onClick();
    } else if (notification.action?.url) {
      if (notification.action.url.startsWith('superadmin:') && onSuperAdminTabClick) {
        const tab = notification.action.url.replace('superadmin:', '');
        onSuperAdminTabClick(tab);
      } else if (notification.action.url === 'tasks' && onTasksClick) {
        onTasksClick();
      } else if (onNavigate) {
        onNavigate(notification.action.url);
      }
    }
    setIsOpen(false);
  };

  const getNotificationIcon = (type: AdminNotification['type']) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'info':
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getPriorityColor = (priority: AdminNotification['priority']) => {
    switch (priority) {
      case 'critical':
        return 'border-red-500 bg-red-50';
      case 'high':
        return 'border-orange-500 bg-orange-50';
      case 'medium':
        return 'border-yellow-500 bg-yellow-50';
      case 'low':
      default:
        return 'border-blue-500 bg-blue-50';
    }
  };

  const notificationCount = AdminNotificationService.getNotificationCount(notifications);
  const highPriorityCount = AdminNotificationService.getHighPriorityCount(notifications);

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
        disabled={isLoading}
      >
        <Bell className="w-6 h-6 text-slate-600" />
        
        {/* Notification Badge */}
        {notificationCount > 0 && (
          <div className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center px-1">
            {notificationCount > 99 ? '99+' : notificationCount}
          </div>
        )}
        
        {/* High Priority Indicator */}
        {highPriorityCount > 0 && (
          <div className="absolute -top-1 -left-1 w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800">
                {isSuperAdmin ? 'Notifications' : 'Admin Notifications'}
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-slate-200 rounded-lg transition-colors duration-200"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            {notificationCount > 0 && (
              <p className="text-sm text-slate-600 mt-1">
                {notificationCount} notification{notificationCount !== 1 ? 's' : ''}
                {highPriorityCount > 0 && (
                  <span className="text-orange-600 font-medium ml-2">
                    ({highPriorityCount} high priority)
                  </span>
                )}
              </p>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-slate-600">Generating notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-slate-800 mb-2">All Good!</h4>
                <p className="text-slate-600">No notifications at this time.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-slate-50 transition-colors duration-200 border-l-4 ${getPriorityColor(notification.priority)}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-slate-800">
                            {notification.title}
                          </h4>
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              notification.priority === 'critical' ? 'bg-red-100 text-red-800' :
                              notification.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                              notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {notification.priority}
                            </span>
                            <button
                              onClick={() => handleDismiss(notification.id)}
                              className="p-1 hover:bg-slate-200 rounded transition-colors duration-200"
                            >
                              <X className="w-3 h-3 text-slate-400" />
                            </button>
                          </div>
                        </div>
                        
                        <p className="text-sm text-slate-600 mt-1">
                          {notification.message}
                        </p>
                        
                        {notification.action && (
                          <button
                            onClick={() => handleAction(notification)}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
                          >
                            {notification.action.label} →
                          </button>
                        )}
                        
                        <p className="text-xs text-slate-400 mt-2">
                          {formatDateTimeUk(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={generateNotifications}
                className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
              >
                Refresh Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
